// The shared branded-PDF toolkit.
//
// One page geometry, one type scale, one writer, used by every document the
// app hands somebody to keep. Two renderers drawing their own margins is how
// a library ends up looking like a library of different houses.
//
// Themes carry the only thing that legitimately differs between them: the
// accent. Holy Static's one-signal rule still holds inside a PDF — CLLXTV
// documents are yellow on ink, Ship Happens is the cover's rust orange, and
// neither ever renders the other's colour.
//
// jsPDF is imported lazily by whoever builds a document. It is a third of a
// megabyte and only the person who presses a save button needs it, so it must
// never ride along on a page load.

export const THEMES = {
  // The hub's own signal, from --yellow in styles.css.
  cllxtv: { accent: [255, 200, 0], accentInk: [15, 15, 15], onAccent: [15, 15, 15] },
  // --ship-accent, eyedroppered from the book cover.
  ship: { accent: [189, 52, 3], accentInk: [228, 85, 42], onAccent: [255, 255, 255] },
};

export const INK = [15, 15, 15];
export const PAPER = [255, 255, 255];
export const DIM = [122, 122, 122];
export const RULE = [200, 200, 196];

export const PAGE = { w: 612, h: 792 }; // US Letter, points
export const MARGIN = 56;
export const WIDTH = PAGE.w - MARGIN * 2;
export const BOTTOM = PAGE.h - MARGIN;

/** Loads an image from the site as a data URL, or null if it cannot. */
export async function imageData(url) {
  try {
    const r = await fetch(url);
    if (!r.ok) return null;
    const blob = await r.blob();
    return await new Promise((resolve) => {
      const fr = new FileReader();
      fr.onload = () => resolve(fr.result);
      fr.onerror = () => resolve(null);
      fr.readAsDataURL(blob);
    });
  } catch {
    return null;
  }
}

/**
 * A cursor that writes down the page and turns it when it runs out.
 *
 * Every block asks for the room it needs before drawing, so a heading is never
 * stranded at the foot of one page with its body on the next.
 */
export class Writer {
  constructor(doc) {
    this.doc = doc;
    this.y = MARGIN;
  }

  page() {
    this.doc.addPage();
    this.y = MARGIN;
  }

  ensure(height) {
    if (this.y + height > BOTTOM) this.page();
  }

  gap(n) {
    this.y += n;
  }

  /** Small uppercase label with the eyebrow's tracking. */
  label(text, color = DIM) {
    const { doc } = this;
    doc.setFont("helvetica", "bold");
    doc.setFontSize(8);
    doc.setTextColor(...color);
    doc.text(text.toUpperCase(), MARGIN, this.y, { charSpace: 1.4 });
    this.y += 13;
  }

  heading(text, size = 22, color = INK) {
    const { doc } = this;
    doc.setFont("helvetica", "bold");
    doc.setFontSize(size);
    doc.setTextColor(...color);
    const lines = doc.splitTextToSize(text.toUpperCase(), WIDTH);
    const lh = size * 1.02;
    this.ensure(lines.length * lh);
    doc.text(lines, MARGIN, this.y + size * 0.8);
    this.y += lines.length * lh + 4;
  }

  body(text, opts = {}) {
    const { doc } = this;
    const size = opts.size || 10.5;
    const lh = size * 1.38;
    doc.setFont("helvetica", opts.bold ? "bold" : opts.italic ? "italic" : "normal");
    doc.setFontSize(size);
    doc.setTextColor(...(opts.color || INK));
    const lines = doc.splitTextToSize(text, opts.width || WIDTH);
    // Long paragraphs may split across a page; that is fine for body copy.
    for (const line of lines) {
      this.ensure(lh);
      doc.text(line, opts.x || MARGIN, this.y + size * 0.85);
      this.y += lh;
    }
    this.y += opts.after ?? 6;
  }

  field(label, text) {
    this.ensure(40);
    this.label(label);
    this.body(text, { after: 8 });
    this.gap(3);
  }

  /** Bold quotation with a rule down its left edge. */
  quote(text) {
    const { doc } = this;
    const size = 14;
    const lh = size * 1.15;
    doc.setFont("helvetica", "bold");
    doc.setFontSize(size);
    const lines = doc.splitTextToSize(text.toUpperCase(), WIDTH - 18);
    const h = lines.length * lh + 8;
    this.ensure(h + 10);
    doc.setDrawColor(...INK);
    doc.setLineWidth(2);
    doc.line(MARGIN, this.y, MARGIN, this.y + h);
    doc.setTextColor(...INK);
    doc.text(lines, MARGIN + 18, this.y + size * 0.9);
    this.y += h + 10;
  }

  /**
   * A prompt, followed by the answer or by lines to write on.
   *
   * Measured in full before anything is drawn, so a question and its lines
   * move to the next page together — a question stranded above three lines
   * on the following page is not something anyone can write on.
   */
  prompt(question, answer) {
    const { doc } = this;
    const size = 10.5;
    doc.setFont("helvetica", "bold");
    doc.setFontSize(size);
    const qLines = doc.splitTextToSize(question, WIDTH).length;
    const written = answer && answer.trim();
    let aLines = 0;
    if (written) {
      doc.setFont("helvetica", "normal");
      aLines = doc.splitTextToSize(written, WIDTH).length;
    }
    const RULED = 5;
    const PITCH = 18;
    const need =
      qLines * size * 1.38 + 4 + (written ? aLines * size * 1.38 + 10 : RULED * PITCH + 12);
    this.ensure(need);

    this.body(question, { bold: true, size, after: 4 });
    if (written) {
      this.body(written, { size, color: [40, 40, 40], after: 10 });
      return;
    }
    doc.setDrawColor(...RULE);
    doc.setLineWidth(0.6);
    for (let i = 0; i < RULED; i++) {
      this.y += PITCH;
      doc.line(MARGIN, this.y, MARGIN + WIDTH, this.y);
    }
    this.y += 12;
  }

  rule(color = RULE) {
    this.ensure(12);
    this.doc.setDrawColor(...color);
    this.doc.setLineWidth(0.6);
    this.doc.line(MARGIN, this.y, MARGIN + WIDTH, this.y);
    this.y += 12;
  }
}

/**
 * Hands the file to the person in whatever way their device can keep it.
 *
 * On a phone the share sheet is the way to a Files app or a drive; a plain
 * download inside an installed PWA often opens the PDF in a dead end with no
 * save control. On a desktop a download is what people expect.
 */
export async function deliver(doc, filename) {
  const blob = doc.output("blob");
  const coarse = (() => {
    try {
      return window.matchMedia("(pointer: coarse)").matches;
    } catch {
      return false;
    }
  })();

  if (coarse && navigator.canShare && typeof File !== "undefined") {
    try {
      const file = new File([blob], filename, { type: "application/pdf" });
      if (navigator.canShare({ files: [file] })) {
        await navigator.share({ files: [file], title: filename });
        return "shared";
      }
    } catch (e) {
      // A dismissed share sheet is not a failure; anything else falls back.
      if (e && e.name === "AbortError") return "cancelled";
    }
  }

  doc.save(filename);
  return "saved";
}

/** A fresh Letter-sized document. jsPDF arrives only when this is called. */
export async function newDoc() {
  const { jsPDF } = await import("jspdf");
  return new jsPDF({ unit: "pt", format: "letter" });
}
